import dayjs from 'dayjs'
import {
  readXeroAutoSendEnabled,
  readXeroAutoSendEnabledForSupplier,
} from 'src/utils/xeroAutoSendCopy'
import { resolveXeroOrganization } from 'src/utils/xeroOrganization'
import {
  OMS_VAT_TREATMENT,
  resolveFinanceDocumentVatTreatment,
  resolveLineVatTreatment,
  resolveOmsVatTreatment,
  resolveXeroPurchaseTaxType,
  readVenueVatTaxMapping,
} from 'src/utils/vatTreatment'
import {
  calculateLineVatPounds,
  moneyEqualsWithinTolerance,
  reconcileLineTaxAmountsToDocumentVat,
  roundDocumentAmounts,
  roundMoneyPounds,
} from 'src/utils/vatRounding'
import { withOmsXeroAuditTrail } from 'src/utils/xeroOmsAudit'
import {
  partitionPendingXeroExports,
  isFinanceDocumentExportedToXero,
} from 'src/utils/xeroExportLifecycle'
import { needsXeroExportResume } from 'src/utils/xeroExportResume'

export const XERO_AUTO_SEND_SKIP_REASON = {
  DISABLED: 'disabled',
  SUPPLIER_DISABLED: 'supplier_disabled',
  NOT_CONNECTED: 'not_connected',
  EXPIRED: 'expired',
  MISSING_DEFAULTS: 'missing_defaults',
  ALREADY_SENT: 'already_sent',
  MISSING_ORGANIZATION: 'missing_organization',
  MISSING_ORDER: 'missing_order',
  /** Order row found but supplier id missing for Xero product fetch. */
  MISSING_SUPPLIER: 'missing_supplier',
  /** Post-reconcile UI could not resolve the PO/order to send. */
  ORDER_NOT_FOUND: 'order_not_found',
}

export function hasSupplierXeroDefaults(venueSupplier = {}) {
  return Boolean(venueSupplier?.defaultAccount && venueSupplier?.defaultContact)
}

/**
 * Xero receives one complete bill only after every PO allocation on an invoice
 * is closed. Partial multi-PO slices must never send early.
 */
export function shouldAutoSendAfterReconcile(result = {}) {
  return !(
    result?.alreadyReconciled ||
    result?.alreadyReconciledAllocation ||
    result?.invoiceFullyReconciled === false
  )
}

export function getXeroAutoSendEligibility({
  venue = {},
  order = {},
  venueSupplier = {},
} = {}) {
  if (!readXeroAutoSendEnabled(venue)) {
    return { eligible: false, reason: XERO_AUTO_SEND_SKIP_REASON.DISABLED }
  }
  if (!readXeroAutoSendEnabledForSupplier(venueSupplier)) {
    return {
      eligible: false,
      reason: XERO_AUTO_SEND_SKIP_REASON.SUPPLIER_DISABLED,
    }
  }
  if (!venue?.xeroConnection?.accessToken) {
    return {
      eligible: false,
      reason: XERO_AUTO_SEND_SKIP_REASON.NOT_CONNECTED,
    }
  }
  if (venue?.xeroConnectionExpired) {
    return { eligible: false, reason: XERO_AUTO_SEND_SKIP_REASON.EXPIRED }
  }
  if (!hasSupplierXeroDefaults(venueSupplier)) {
    return {
      eligible: false,
      reason: XERO_AUTO_SEND_SKIP_REASON.MISSING_DEFAULTS,
    }
  }
  if (order?.xeroInvoiceId || order?.sentToXero) {
    return {
      eligible: false,
      reason: XERO_AUTO_SEND_SKIP_REASON.ALREADY_SENT,
    }
  }
  const destinationOrg = resolveXeroOrganization(venue?.xeroConnection)
  if (!destinationOrg?.id) {
    return {
      eligible: false,
      reason: XERO_AUTO_SEND_SKIP_REASON.MISSING_ORGANIZATION,
    }
  }
  if (
    !Array.isArray(order?.orderIds) ||
    !order.orderIds.length ||
    !order?.purchaseOrderNumber
  ) {
    return {
      eligible: false,
      reason: XERO_AUTO_SEND_SKIP_REASON.MISSING_ORDER,
    }
  }
  return { eligible: true, reason: null, organization: destinationOrg }
}

/**
 * Build Xero Reference for ACCPAY bills.
 * Prefer the supplier invoice number (Xero UI "Reference" field). Keep the OMS
 * PO separately visible when it differs from the supplier number.
 */
export function buildXeroBillReference({
  purchaseOrderNumber,
  venueTitle,
  supplierInvoiceNumber,
} = {}) {
  const supplierRef = String(supplierInvoiceNumber || '').trim()
  const po = String(purchaseOrderNumber || '').trim()
  const venue = String(venueTitle || '').trim()

  if (supplierRef) {
    if (po && po !== supplierRef) {
      return `${supplierRef} (OMS PO ${po})`
    }
    return supplierRef
  }

  return `OMS - ${po} - ${venue}`
    .replace(/\s+-\s+$/, '')
    .trim()
}

/**
 * Resolve the supplier's invoice/credit number for Xero.
 * Never falls back to the OMS purchase order number.
 */
export function resolveXeroSupplierInvoiceNumber({
  invoiceNumber,
  order = {},
  invoice = {},
} = {}) {
  const purchaseOrderNumber = String(order?.purchaseOrderNumber || '').trim()
  const detailNumbers = Array.isArray(order?.attachedInvoiceDetails)
    ? order.attachedInvoiceDetails.map(detail => detail?.invoiceNumber)
    : []
  const candidates = [
    invoiceNumber,
    invoice?.invoiceNumber,
    order?.invoiceNumber,
    order?.attachedInvoiceNumber,
    ...detailNumbers,
    order?.receipting?.invoiceNumber,
  ]

  for (const value of candidates) {
    const trimmed = String(value ?? '').trim()
    if (!trimmed) continue
    // Guard: attach helpers can store PO as invoiceNumber when the supplier
    // number is missing — do not send that to Xero as InvoiceNumber.
    if (purchaseOrderNumber && trimmed === purchaseOrderNumber) continue
    return trimmed
  }

  return null
}

/**
 * Apply supplier invoice number to Xero InvoiceNumber/CreditNoteNumber and
 * Reference (the labeled field on the bill form).
 */
export function withXeroSupplierInvoiceNumber(
  billData,
  supplierInvoiceNumber,
  { purchaseOrderNumber, venueTitle } = {},
) {
  if (!billData) return billData
  const number = String(supplierInvoiceNumber || '').trim()
  const next = {
    ...billData,
    Reference: buildXeroBillReference({
      purchaseOrderNumber:
        purchaseOrderNumber ??
        billData.purchaseOrderNumber ??
        undefined,
      venueTitle,
      supplierInvoiceNumber: number,
    }),
  }

  // Avoid leaking a non-API helper field if present.
  delete next.purchaseOrderNumber

  if (!number) return next
  if (next.Type === 'ACCPAYCREDIT') {
    return { ...next, CreditNoteNumber: number }
  }
  return { ...next, InvoiceNumber: number }
}

/**
 * OMS → Xero line AccountID precedence (existing production layers):
 *
 * 1. VenueProduct.nominalCode → match Xero account `Code` in the destination org
 *    - If the product has a nominal code that does NOT resolve → fail closed
 *      (never silently substitute supplier/venue defaults)
 * 2. Supplier VenueSupplier.defaultAccount
 *    (or Send-to-Xero UI selection used as the supplier-default override)
 * 3. forceSend only: venue.xeroConnection.fallbackPurchasesAccountId
 * 4. Else fail
 *
 * Invoice-level account coding does not exist in OMS today.
 */
export function normaliseXeroAccountCode(value) {
  return String(value ?? '')
    .trim()
    .toUpperCase()
}

export function indexXeroAccountsByCode(accounts = []) {
  const map = new Map()
  for (const account of Array.isArray(accounts) ? accounts : []) {
    const code = normaliseXeroAccountCode(account?.Code)
    const accountId = String(account?.AccountID || '').trim()
    if (!code || !accountId) continue
    const status = String(account?.Status || '').toUpperCase()
    if (status && status !== 'ACTIVE') continue
    if (!map.has(code)) map.set(code, account)
  }
  return map
}

export function resolveXeroLineAccountId({
  line = {},
  venueSupplier = {},
  venue = {},
  accountsByCode = new Map(),
  forceSend = false,
  /** Supplier default, or UI override for lines without a product nominal code. */
  supplierDefaultAccountId = null,
} = {}) {
  const productTitle = line?.product?.title || line?.title || 'Product'
  const nominalCode = String(
    line?.product?.nominalCode ?? line?.nominalCode ?? '',
  ).trim()

  if (nominalCode) {
    const matched = accountsByCode.get(normaliseXeroAccountCode(nominalCode))
    if (matched?.AccountID) {
      return {
        accountId: matched.AccountID,
        source: 'product_nominal_code',
        nominalCode,
      }
    }
    const err = new Error(
      `Product "${productTitle}" could not be exported because Xero account code ${nominalCode} is unavailable in the connected organisation.`,
    )
    err.code = 'XERO_ACCOUNT_CODE_UNAVAILABLE'
    err.productTitle = productTitle
    err.nominalCode = nominalCode
    throw err
  }

  const supplierAccount =
    supplierDefaultAccountId || venueSupplier?.defaultAccount || null
  if (supplierAccount) {
    return {
      accountId: supplierAccount,
      source: 'supplier_default',
      nominalCode: null,
    }
  }

  const venueFallback =
    venue?.xeroConnection?.fallbackPurchasesAccountId || null
  if (forceSend && venueFallback) {
    return {
      accountId: venueFallback,
      source: 'venue_fallback',
      nominalCode: null,
    }
  }

  const err = new Error(
    `Product "${productTitle}" could not be exported because no Xero account is mapped for this supplier (and no product nominal code is set).`,
  )
  err.code = 'XERO_ACCOUNT_MISSING'
  err.productTitle = productTitle
  throw err
}

export function buildXeroBillLineItems({
  rows = [],
  venueSupplier = {},
  venue = {},
  xeroAccounts = [],
  forceSend = false,
  supplierDefaultAccountId = null,
  isCreditNote = false,
  xeroTaxRates = [],
} = {}) {
  const accountsByCode = indexXeroAccountsByCode(xeroAccounts)
  const { taxTypeByTreatment } = readVenueVatTaxMapping(
    venue?.xeroConnection || {},
  )
  return rows.map(item => {
    const { accountId } = resolveXeroLineAccountId({
      line: item,
      venueSupplier,
      venue,
      accountsByCode,
      forceSend,
      supplierDefaultAccountId,
    })
    const baseQuantity = item?.quantity ?? 0
    const baseUnitAmount =
      item?.receipting?.updatedPrice != null
        ? item.receipting.updatedPrice
        : item?.product?.price ?? item?.price ?? 0
    const quantity = isCreditNote ? Math.abs(baseQuantity) : baseQuantity
    const unitAmount = roundMoneyPounds(
      isCreditNote ? Math.abs(baseUnitAmount) : baseUnitAmount,
    )
    const treatment = resolveLineVatTreatment(item)
    const netLine = roundMoneyPounds(unitAmount * (Number(quantity) || 0))
    const taxAmount = calculateLineVatPounds(netLine, { treatment })

    return {
      Description: item?.product?.title,
      Quantity: quantity,
      UnitAmount: unitAmount,
      TaxAmount: taxAmount,
      AccountID: accountId,
      TaxType: resolveXeroPurchaseTaxType(
        treatment,
        xeroTaxRates,
        taxTypeByTreatment,
        { xeroConnection: venue?.xeroConnection },
      ),
    }
  })
}

/**
 * Part 10 — when the supplier document carries a VAT total, reconcile line
 * TaxAmounts so a legitimate ±1–2p rounding difference does not fail export.
 * Material mismatches throw (XERO_VAT_MATERIAL_DISCREPANCY).
 */
export function applyDocumentVatRounding(billData = {}, document = {}) {
  if (!billData || !Array.isArray(billData.LineItems)) return billData
  const amounts = roundDocumentAmounts(getFinanceDocumentAmountsPounds(document))
  if (amounts?.vat == null) return billData

  const result = reconcileLineTaxAmountsToDocumentVat(
    billData.LineItems,
    amounts.vat,
  )
  return {
    ...billData,
    LineItems: result.lineItems,
    vatRounding: {
      status: result.assessment?.status || null,
      adjusted: result.adjusted,
      adjustmentPennies: result.adjustmentPennies || 0,
      documentVatPounds: result.documentVatPounds,
      lineVatSumPounds: result.lineVatSumPounds,
    },
  }
}

/**
 * Build ACCPAY bill. Requires a Xero ContactID + AccountID unless forceSend
 * (force uses Name (OMS) and mapped/fallback account for archived/missing contacts).
 *
 * Contact/account may come from:
 * - VenueSupplier.defaultContact / defaultAccount, OR
 * - Send-to-Xero UI dropdown selection (selectedContactId / supplierDefaultAccountId)
 *
 * Mapping:
 * - Supplier invoice # → Xero InvoiceNumber + Reference
 * - OMS PO kept on Reference as "(OMS PO …)" when present
 * - Line AccountID → product nominalCode, else supplier default (see resolveXeroLineAccountId)
 */
export function buildReconciledXeroBillData({
  venue = {},
  order = {},
  venueSupplier = {},
  receiptingProducts = [],
  forceSend = false,
  invoiceNumber,
  invoice,
  xeroAccounts = [],
  supplierDefaultAccountId = null,
  /** Send-to-Xero UI contact selection when supplier has no defaultContact. */
  selectedContactId = null,
  xeroTaxRates = [],
} = {}) {
  const rows = (
    Array.isArray(receiptingProducts) ? receiptingProducts.flat() : []
  ).filter(row => row?.supplierConfirmed === true)

  if (!rows.length) {
    throw new Error('No confirmed receipting lines were found for this order.')
  }

  const supplierName =
    venueSupplier?.supplierTitle ||
    venueSupplier?.title ||
    order?.supplier?.title ||
    order?.supplierTitle ||
    'Supplier'
  const mappedContact = venueSupplier?.defaultContact
  const mappedAccount = venueSupplier?.defaultAccount
  const venueFallbackAccountId =
    venue?.xeroConnection?.fallbackPurchasesAccountId || null
  const contactId = String(selectedContactId || mappedContact || '').trim()

  if (!forceSend && !contactId) {
    throw new Error(
      'Select a Xero contact before sending, or map a default contact under Your Suppliers.',
    )
  }

  let Contact
  if (forceSend) {
    Contact = { Name: `${supplierName} (OMS)` }
  } else {
    Contact = { ContactID: contactId }
  }

  const defaultAccountId =
    supplierDefaultAccountId ||
    mappedAccount ||
    (forceSend ? venueFallbackAccountId : null)
  if (!defaultAccountId) {
    // Still allow build when every line has a resolvable product nominal code;
    // resolveXeroLineAccountId will throw a clearer per-product error if not.
    const hasAnyProductCode = rows.some(row =>
      String(row?.product?.nominalCode || row?.nominalCode || '').trim(),
    )
    if (!hasAnyProductCode) {
      throw new Error(
        'Select a Xero account before sending, or map a default account under Your Suppliers.',
      )
    }
  }

  const supplierInvoiceNumber = resolveXeroSupplierInvoiceNumber({
    invoiceNumber,
    order,
    invoice,
  })

  const billData = {
    Type: 'ACCPAY',
    Contact,
    Date: dayjs(order.orderedDate).format('YYYY-MM-DD'),
    DueDate: dayjs(order.orderedDate).format('YYYY-MM-DD'),
    LineAmountTypes: 'Exclusive',
    LineItems: buildXeroBillLineItems({
      rows,
      venueSupplier,
      venue,
      xeroAccounts,
      forceSend,
      supplierDefaultAccountId: defaultAccountId,
      xeroTaxRates,
    }),
    Status: 'AUTHORISED',
  }

  const withNumber = withXeroSupplierInvoiceNumber(billData, supplierInvoiceNumber, {
    purchaseOrderNumber: order?.purchaseOrderNumber,
    venueTitle: venue?.title,
  })

  // Part 10 — reconcile line VAT to supplier invoice VAT when present.
  const rounded = applyDocumentVatRounding(withNumber, invoice)
  return withOmsXeroAuditTrail(rounded, {
    venue,
    order,
    venueSupplier,
    document: invoice,
    receiptingProducts: rows,
  })
}

/**
 * True when an OMS Invoice document is a credit note (not a negative GR order).
 */
export function isCreditNoteFinanceDocument(document = {}) {
  const type = String(document?.documentType || 'invoice')
    .trim()
    .toLowerCase()
  return type === 'creditnote' || type === 'credit_note'
}

export function partitionFinanceDocuments(documents = []) {
  const invoices = []
  const creditNotes = []
  for (const doc of Array.isArray(documents) ? documents : []) {
    if (!doc || doc.isArchived) continue
    if (isCreditNoteFinanceDocument(doc)) {
      creditNotes.push(doc)
    } else {
      invoices.push(doc)
    }
  }
  return { invoices, creditNotes }
}

/**
 * Absolute document face value in pounds (from pennies invoiceTotal).
 */
export function getFinanceDocumentTotalPounds(document = {}) {
  const amounts = getFinanceDocumentAmountsPounds(document)
  return amounts?.gross ?? null
}

/**
 * Net / VAT / gross from the finance document (pennies → pounds).
 * Does **not** invent VAT from gross alone (e.g. assume 20%).
 *
 * @returns {{ gross: number|null, net: number|null, vat: number|null, source: string }|null}
 */
export function getFinanceDocumentAmountsPounds(document = {}) {
  const toPounds = pennies => {
    const n = Number(pennies)
    if (!Number.isFinite(n) || n === 0) return null
    return Math.abs(n) / 100
  }
  // Allow explicit 0 VAT (zero-rated / no VAT).
  const toPoundsAllowZero = pennies => {
    const n = Number(pennies)
    if (!Number.isFinite(n)) return null
    return Math.abs(n) / 100
  }

  let gross = toPounds(document?.invoiceTotal)
  let net = toPounds(document?.invoiceTotalNet)
  let vat = toPoundsAllowZero(document?.invoiceVatTotal)

  // OCR suggested fallbacks when canonical fields missing
  if (gross == null) gross = toPounds(document?.ocrSuggested?.grossTotal)
  if (net == null) net = toPounds(document?.ocrSuggested?.netTotal)
  if (vat == null) vat = toPoundsAllowZero(document?.ocrSuggested?.vatTotal)

  if (gross == null && net == null && vat == null) return null

  const round2 = n => Math.round(n * 100) / 100

  if (gross != null && vat != null && net == null) {
    return { gross, net: round2(gross - vat), vat, source: 'gross_and_vat' }
  }
  if (net != null && vat != null && gross == null) {
    return { gross: round2(net + vat), net, vat, source: 'net_and_vat' }
  }
  if (net != null && gross != null && vat == null) {
    return {
      gross,
      net,
      vat: round2(Math.max(0, gross - net)),
      source: 'gross_and_net',
    }
  }
  if (gross != null && net != null && vat != null) {
    return { gross, net, vat, source: 'gross_net_vat' }
  }
  if (gross != null) {
    return { gross, net: null, vat: null, source: 'gross_only' }
  }
  if (net != null) {
    return { gross: net, net, vat: 0, source: 'net_only' }
  }
  return { gross: vat, net: 0, vat, source: 'vat_only' }
}

/**
 * Normalise optional per-line credit-note rows from the supplier document.
 * Accepts OMS/OCR shapes: lineItems, creditNoteLines, or ocr line payloads.
 */
export function getCreditNoteDocumentLines(document = {}) {
  const raw =
    (Array.isArray(document?.lineItems) && document.lineItems) ||
    (Array.isArray(document?.creditNoteLines) && document.creditNoteLines) ||
    (Array.isArray(document?.ocrSuggested?.lineItems) &&
      document.ocrSuggested.lineItems) ||
    []

  return raw
    .map((line, index) => {
      if (!line || typeof line !== 'object') return null
      const treatment = resolveOmsVatTreatment({
        vatTreatment: line.vatTreatment || line.taxTreatment,
        hasVat:
          line.hasVat === true
            ? true
            : line.hasVat === false
              ? false
              : undefined,
      })
      const net =
        penniesOrPoundsToPounds(line.netPennies ?? line.net ?? line.netAmount) ??
        (line.amountExVat != null
          ? penniesOrPoundsToPounds(line.amountExVat)
          : null)
      const vat = penniesOrPoundsToPoundsAllowZero(
        line.vatPennies ?? line.vat ?? line.vatAmount ?? line.taxAmount,
      )
      const gross =
        penniesOrPoundsToPounds(
          line.grossPennies ?? line.gross ?? line.amount ?? line.lineAmount,
        ) ?? null

      let unitNet = net
      let unitVat = vat
      if (unitNet == null && gross != null && unitVat != null) {
        unitNet = Math.round((gross - unitVat) * 100) / 100
      }
      if (unitNet == null && gross != null && treatment !== OMS_VAT_TREATMENT.STANDARD) {
        unitNet = gross
        unitVat = 0
      }
      if (unitNet == null) return null

      return {
        description:
          line.description ||
          line.title ||
          line.product?.title ||
          `Credit line ${index + 1}`,
        net: Math.abs(unitNet),
        vat: unitVat != null ? Math.abs(unitVat) : 0,
        vatTreatment: treatment,
        nominalCode:
          line.nominalCode || line.product?.nominalCode || line.accountCode || null,
        quantity: Math.abs(Number(line.quantity) || 1) || 1,
      }
    })
    .filter(Boolean)
}

function penniesOrPoundsToPounds(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return null
  // Heuristic: integers with |n| >= 1000 and no decimal likely pennies
  if (Number.isInteger(n) && Math.abs(n) >= 1000) return Math.abs(n) / 100
  return Math.abs(n)
}

function penniesOrPoundsToPoundsAllowZero(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  if (n === 0) return 0
  if (Number.isInteger(n) && Math.abs(n) >= 1000) return Math.abs(n) / 100
  return Math.abs(n)
}

/**
 * Resolve which Xero *supplier bill* (ACCPAY InvoiceID) a credit note should
 * allocate against. Prefer soft-link, then any sibling invoice already in Xero.
 */
export function resolveXeroCreditAllocationTarget({
  creditNote = {},
  invoiceDocuments = [],
  fallbackXeroInvoiceId = null,
} = {}) {
  const softLinkId = String(
    creditNote?.softLinkedInvoiceId?._id ||
      creditNote?.softLinkedInvoiceId ||
      '',
  ).trim()
  if (softLinkId) {
    const linked = (invoiceDocuments || []).find(
      doc => String(doc?._id || doc?.id) === softLinkId,
    )
    const fromSoft = String(linked?.xeroInvoiceId || '').trim()
    if (fromSoft) return fromSoft
  }

  for (const doc of invoiceDocuments || []) {
    if (isCreditNoteFinanceDocument(doc)) continue
    const xeroId = String(doc?.xeroInvoiceId || '').trim()
    if (xeroId && doc?.xeroInvoiceType !== 'CreditNote') {
      return xeroId
    }
  }

  const fallback = String(fallbackXeroInvoiceId || '').trim()
  return fallback || null
}

/**
 * Gross credit amount to allocate (positive pounds).
 * Uses RemainingCredit / Total when present; else net + VAT on Exclusive lines.
 */
export function getXeroCreditAllocationAmount(billData = {}, createdRecord = {}) {
  const remaining = Number(createdRecord?.RemainingCredit)
  if (Number.isFinite(remaining) && remaining > 0) return remaining
  const total = Number(createdRecord?.Total)
  if (Number.isFinite(total) && total !== 0) return Math.abs(total)
  const lines = Array.isArray(billData?.LineItems) ? billData.LineItems : []
  const fromLines = lines.reduce((sum, line) => {
    const qty = Number(line?.Quantity)
    const unit = Number(line?.UnitAmount)
    const tax = Number(line?.TaxAmount)
    if (!Number.isFinite(unit)) return sum
    const lineNet =
      Math.abs(unit) * (Number.isFinite(qty) && qty !== 0 ? Math.abs(qty) : 1)
    const lineTax = Number.isFinite(tax) ? Math.abs(tax) : 0
    return sum + lineNet + lineTax
  }, 0)
  return fromLines > 0 ? fromLines : null
}

function buildContactForXeroBill({
  venueSupplier = {},
  order = {},
  forceSend = false,
  selectedContactId = null,
} = {}) {
  const supplierName =
    venueSupplier?.supplierTitle ||
    venueSupplier?.title ||
    order?.supplier?.title ||
    order?.supplierTitle ||
    'Supplier'
  if (forceSend) {
    return { Name: `${supplierName} (OMS)` }
  }
  const contactId = String(
    selectedContactId || venueSupplier?.defaultContact || '',
  ).trim()
  if (contactId) {
    return { ContactID: contactId }
  }
  return { Name: supplierName }
}

/**
 * Confirmed GR lines for Xero export (supplierConfirmed only).
 */
export function getConfirmedReceiptingRows(receiptingProducts = []) {
  return (Array.isArray(receiptingProducts) ? receiptingProducts.flat() : []).filter(
    row => row?.supplierConfirmed === true,
  )
}

/**
 * True when confirmed lines use more than one OMS VAT treatment.
 */
export function hasMixedVatTreatmentsOnLines(rows = []) {
  const treatments = new Set(
    (Array.isArray(rows) ? rows : []).map(row => resolveLineVatTreatment(row)),
  )
  return treatments.size > 1
}

/**
 * Build supplier *invoice* bill for Xero.
 *
 * Part 8 — when GR lines exist, export one Xero line per product so mixed
 * nominal codes AND mixed VAT treatments are preserved. Face-value summary
 * is only a fallback when there are no confirmed lines (or for credit notes).
 */
export function buildXeroInvoiceExportBill({
  document = {},
  venue = {},
  order = {},
  venueSupplier = {},
  receiptingProducts = [],
  forceSend = false,
  xeroAccounts = [],
  supplierDefaultAccountId = null,
  selectedContactId = null,
  dueDate = null,
  xeroTaxRates = [],
  invoiceNumber,
} = {}) {
  if (isCreditNoteFinanceDocument(document)) {
    return buildXeroCreditNoteExportBill({
      document,
      venue,
      order,
      venueSupplier,
      receiptingProducts,
      forceSend,
      xeroAccounts,
      supplierDefaultAccountId,
      selectedContactId,
      dueDate,
      xeroTaxRates,
    })
  }

  const rows = getConfirmedReceiptingRows(receiptingProducts)
  if (rows.length > 0) {
    const billData = buildReconciledXeroBillData({
      venue,
      order,
      venueSupplier,
      receiptingProducts,
      forceSend,
      invoiceNumber:
        invoiceNumber ||
        document?.invoiceNumber ||
        undefined,
      invoice: document,
      xeroAccounts,
      supplierDefaultAccountId,
      selectedContactId,
      xeroTaxRates,
    })
    if (dueDate) {
      billData.DueDate = dayjs(dueDate).format('YYYY-MM-DD')
    }
    // Prefer document date when the finance document carries one.
    const docDate = document?.invoiceDate
    if (docDate && dayjs(docDate).isValid()) {
      billData.Date = dayjs(docDate).format('YYYY-MM-DD')
    }
    return billData
  }

  // No GR lines — face-value document export when we have a finance document.
  if (
    document &&
    (document._id ||
      document.id ||
      document.invoiceNumber ||
      document.invoiceTotal != null)
  ) {
    return buildXeroBillFromFinanceDocument({
      document,
      venue,
      order,
      venueSupplier,
      forceSend,
      xeroAccounts,
      supplierDefaultAccountId,
      selectedContactId,
      dueDate,
      xeroTaxRates,
    })
  }

  // Preserve previous clear error when neither lines nor a document exist.
  return buildReconciledXeroBillData({
    venue,
    order,
    venueSupplier,
    receiptingProducts,
    forceSend,
    invoiceNumber,
    invoice: document,
    xeroAccounts,
    supplierDefaultAccountId,
    selectedContactId,
    xeroTaxRates,
  })
}

/**
 * Summary LineItems with correct VAT. Standard-rated uses Exclusive net +
 * explicit TaxAmount — never invent VAT from gross alone (Part 9).
 */
export function buildFinanceDocumentSummaryLineItems({
  document = {},
  description,
  accountId,
  xeroTaxRates = [],
  venue = {},
  isCredit = false,
} = {}) {
  const amounts = getFinanceDocumentAmountsPounds(document)
  const treatment = resolveFinanceDocumentVatTreatment(document)
  const taxType = resolveXeroPurchaseTaxType(treatment, xeroTaxRates, null, {
    xeroConnection: venue?.xeroConnection,
  })
  const label = description || (isCredit ? 'Credit note' : 'Invoice')

  if (!amounts) {
    throw new Error(
      `Cannot export ${isCredit ? 'credit note' : 'invoice'} — missing document totals.`,
    )
  }

  if (treatment === OMS_VAT_TREATMENT.STANDARD) {
    if (amounts.net == null || amounts.vat == null) {
      throw new Error(
        `Cannot export ${isCredit ? 'credit note' : 'invoice'} ${
          document?.invoiceNumber || ''
        } — standard-rated document is missing net and/or VAT totals (will not invent VAT from gross alone).`
          .replace(/\s+/g, ' ')
          .trim(),
      )
    }
    // Part 10 — keep supplier VAT; only fail when it is materially wrong vs net×rate.
    const calculatedVat = calculateLineVatPounds(amounts.net, {
      treatment: OMS_VAT_TREATMENT.STANDARD,
    })
    const reconciled = reconcileLineTaxAmountsToDocumentVat(
      [
        {
          Description: label,
          Quantity: 1,
          UnitAmount: amounts.net,
          TaxAmount: calculatedVat,
          AccountID: accountId,
          TaxType: taxType,
        },
      ],
      amounts.vat,
    )
    return {
      LineAmountTypes: 'Exclusive',
      LineItems: reconciled.lineItems,
      amounts,
      treatment,
      vatRounding: {
        status: reconciled.assessment?.status || null,
        adjusted: reconciled.adjusted,
        adjustmentPennies: reconciled.adjustmentPennies || 0,
      },
    }
  }

  const unit =
    amounts.net != null
      ? amounts.net
      : amounts.gross != null
        ? amounts.gross
        : null
  if (unit == null) {
    throw new Error(
      `Cannot export ${isCredit ? 'credit note' : 'invoice'} — missing document total.`,
    )
  }
  return {
    LineAmountTypes: 'Exclusive',
    LineItems: [
      {
        Description: label,
        Quantity: 1,
        UnitAmount: unit,
        TaxAmount: 0,
        AccountID: accountId,
        TaxType: taxType,
      },
    ],
    amounts,
    treatment,
  }
}

export function buildXeroCreditNoteFromDocumentLines({
  document = {},
  lines = [],
  venue = {},
  order = {},
  venueSupplier = {},
  forceSend = false,
  xeroAccounts = [],
  supplierDefaultAccountId = null,
  selectedContactId = null,
  dueDate = null,
  allocateToXeroInvoiceId = null,
  xeroTaxRates = [],
} = {}) {
  const accountsByCode = indexXeroAccountsByCode(xeroAccounts)
  const defaultAccountId =
    supplierDefaultAccountId ||
    venueSupplier?.defaultAccount ||
    (forceSend
      ? venue?.xeroConnection?.fallbackPurchasesAccountId || null
      : null)

  const LineItems = lines.map(line => {
    const { accountId } = resolveXeroLineAccountId({
      line: {
        product: {
          title: line.description,
          nominalCode: line.nominalCode,
        },
      },
      venueSupplier,
      venue,
      accountsByCode,
      forceSend,
      supplierDefaultAccountId: defaultAccountId,
    })
    const taxType = resolveXeroPurchaseTaxType(
      line.vatTreatment,
      xeroTaxRates,
      null,
      { xeroConnection: venue?.xeroConnection },
    )
    const qty = line.quantity || 1
    const unitAmount = roundMoneyPounds(line.net / qty)
    // Prefer supplier line VAT; else calculate (line-level rounding).
    const taxAmount =
      line.vat != null
        ? roundMoneyPounds(line.vat)
        : calculateLineVatPounds(roundMoneyPounds(unitAmount * qty), {
            treatment: line.vatTreatment,
          })
    return {
      Description: line.description,
      Quantity: qty,
      UnitAmount: unitAmount,
      TaxAmount: taxAmount,
      AccountID: accountId,
      TaxType: taxType,
    }
  })

  const docDate = document?.invoiceDate || order?.orderedDate
  const billDate = dayjs(docDate).isValid()
    ? dayjs(docDate).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD')
  const number = String(document?.invoiceNumber || '').trim()

  const billData = {
    Type: 'ACCPAYCREDIT',
    Contact: buildContactForXeroBill({
      venueSupplier,
      order,
      forceSend,
      selectedContactId,
    }),
    Date: billDate,
    DueDate: dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : billDate,
    LineAmountTypes: 'Exclusive',
    LineItems,
    Status: 'AUTHORISED',
  }

  const withNumber = withXeroSupplierInvoiceNumber(billData, number, {
    purchaseOrderNumber: order?.purchaseOrderNumber,
    venueTitle: venue?.title,
  })
  if (allocateToXeroInvoiceId) {
    withNumber.allocateToXeroInvoiceId = String(allocateToXeroInvoiceId)
  }
  // Part 10 — absorb penny VAT drift vs document VAT total when present.
  const rounded = applyDocumentVatRounding(withNumber, document)
  return withOmsXeroAuditTrail(rounded, {
    venue,
    order,
    venueSupplier,
    document,
    receiptingProducts: [],
  })
}

/**
 * Use GR lines for a CN when document net matches line ex-VAT sum (full credit).
 */
export function shouldUseReceiptingLinesForCreditNote(document = {}, rows = []) {
  if (!rows?.length) return false
  const amounts = getFinanceDocumentAmountsPounds(document)
  if (amounts?.net == null) return false

  const netSum = rows.reduce((sum, row) => {
    const qty = Number(row?.quantity) || 0
    const price =
      row?.receipting?.updatedPrice != null
        ? Number(row.receipting.updatedPrice)
        : Number(row?.product?.price ?? row?.price ?? 0)
    if (!Number.isFinite(price) || !Number.isFinite(qty)) return sum
    return sum + Math.abs(price) * Math.abs(qty)
  }, 0)
  if (!(netSum > 0)) return false

  return moneyEqualsWithinTolerance(amounts.net, netSum)
}

/**
 * Part 9 — ACCPAYCREDIT with VAT preserved (net + TaxAmount, or mixed lines).
 */
export function buildXeroCreditNoteExportBill({
  document = {},
  venue = {},
  order = {},
  venueSupplier = {},
  receiptingProducts = [],
  forceSend = false,
  xeroAccounts = [],
  supplierDefaultAccountId = null,
  selectedContactId = null,
  dueDate = null,
  allocateToXeroInvoiceId = null,
  xeroTaxRates = [],
} = {}) {
  const docLines = getCreditNoteDocumentLines(document)
  if (docLines.length > 0) {
    return buildXeroCreditNoteFromDocumentLines({
      document,
      lines: docLines,
      venue,
      order,
      venueSupplier,
      forceSend,
      xeroAccounts,
      supplierDefaultAccountId,
      selectedContactId,
      dueDate,
      allocateToXeroInvoiceId,
      xeroTaxRates,
    })
  }

  const rows = getConfirmedReceiptingRows(receiptingProducts)
  if (rows.length > 0 && shouldUseReceiptingLinesForCreditNote(document, rows)) {
    const billData = buildReconciledXeroBillData({
      venue,
      order,
      venueSupplier,
      receiptingProducts,
      forceSend,
      invoiceNumber: document?.invoiceNumber,
      invoice: document,
      xeroAccounts,
      supplierDefaultAccountId,
      selectedContactId,
      xeroTaxRates,
    })
    billData.Type = 'ACCPAYCREDIT'
    if (dueDate) {
      billData.DueDate = dayjs(dueDate).format('YYYY-MM-DD')
    }
    const docDate = document?.invoiceDate
    if (docDate && dayjs(docDate).isValid()) {
      billData.Date = dayjs(docDate).format('YYYY-MM-DD')
    }
    const withNumber = withXeroSupplierInvoiceNumber(
      billData,
      document?.invoiceNumber,
      {
        purchaseOrderNumber: order?.purchaseOrderNumber,
        venueTitle: venue?.title,
      },
    )
    if (allocateToXeroInvoiceId) {
      withNumber.allocateToXeroInvoiceId = String(allocateToXeroInvoiceId)
    }
    return withNumber
  }

  return buildXeroBillFromFinanceDocument({
    document,
    venue,
    order,
    venueSupplier,
    forceSend,
    xeroAccounts,
    supplierDefaultAccountId,
    selectedContactId,
    dueDate,
    allocateToXeroInvoiceId,
    xeroTaxRates,
  })
}

/**
 * Build one Xero ACCPAY / ACCPAYCREDIT from an OMS finance document.
 * Standard-rated: Exclusive net + TaxAmount (Part 9). Credit notes use ACCPAYCREDIT.
 */
export function buildXeroBillFromFinanceDocument({
  document = {},
  venue = {},
  order = {},
  venueSupplier = {},
  forceSend = false,
  xeroAccounts = [],
  supplierDefaultAccountId = null,
  selectedContactId = null,
  dueDate = null,
  allocateToXeroInvoiceId = null,
  xeroTaxRates = [],
} = {}) {
  const isCredit = isCreditNoteFinanceDocument(document)
  const amounts = getFinanceDocumentAmountsPounds(document)
  if (!amounts || (amounts.gross == null && amounts.net == null)) {
    throw new Error(
      `Cannot export ${isCredit ? 'credit note' : 'invoice'} ${
        document?.invoiceNumber || ''
      } to Xero — missing document total.`.replace(/\s+/g, ' ').trim(),
    )
  }

  const mappedAccount = venueSupplier?.defaultAccount
  const venueFallback =
    venue?.xeroConnection?.fallbackPurchasesAccountId || null
  const defaultAccountId =
    supplierDefaultAccountId ||
    mappedAccount ||
    (forceSend ? venueFallback : null)

  const { accountId } = resolveXeroLineAccountId({
    line: { product: { title: document?.invoiceNumber || 'Document' } },
    venueSupplier,
    venue,
    accountsByCode: indexXeroAccountsByCode(xeroAccounts),
    forceSend,
    supplierDefaultAccountId: defaultAccountId,
  })

  const number = String(document?.invoiceNumber || '').trim()
  const description = isCredit
    ? `Credit note ${number}`.trim()
    : `Invoice ${number}`.trim()

  const summary = buildFinanceDocumentSummaryLineItems({
    document,
    description: description || (isCredit ? 'Credit note' : 'Invoice'),
    accountId,
    xeroTaxRates,
    venue,
    isCredit,
  })

  const docDate = document?.invoiceDate || order?.orderedDate
  const billDate = dayjs(docDate).isValid()
    ? dayjs(docDate).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD')
  const billDue = dueDate
    ? dayjs(dueDate).format('YYYY-MM-DD')
    : billDate

  const billData = {
    Type: isCredit ? 'ACCPAYCREDIT' : 'ACCPAY',
    Contact: buildContactForXeroBill({
      venueSupplier,
      order,
      forceSend,
      selectedContactId,
    }),
    Date: billDate,
    DueDate: billDue,
    LineAmountTypes: summary.LineAmountTypes,
    LineItems: summary.LineItems,
    Status: 'AUTHORISED',
  }

  const withNumber = withXeroSupplierInvoiceNumber(billData, number, {
    purchaseOrderNumber: order?.purchaseOrderNumber,
    venueTitle: venue?.title,
  })

  if (isCredit && allocateToXeroInvoiceId) {
    withNumber.allocateToXeroInvoiceId = String(allocateToXeroInvoiceId)
  }
  return withOmsXeroAuditTrail(withNumber, {
    venue,
    order,
    venueSupplier,
    document,
    receiptingProducts: [],
  })
}

/**
 * Decide export jobs for a PO.
 *
 * Part 15 — once a supplier invoice is exported as a Xero Bill, later credit
 * notes must create a *new* ACCPAYCREDIT. Never delete, amend, or recreate the
 * original bill (e.g. do not turn £120 bill into £100).
 *
 * Part 16 — docs with xeroInvoiceId but incomplete post-create steps are
 * re-queued as resume jobs (skip create; retry attachment / history / allocation).
 *
 * - Pending invoice docs → ACCPAY create (skip if already have xeroInvoiceId)
 * - Pending credit-note docs → ACCPAYCREDIT create + allocate to existing bill
 * - Incomplete exports → resume job (same selectedInvoiceId, resumeExport: true)
 * - Fully-exported docs → omitted from jobs (idempotent)
 * - GR-only (no finance docs) → line-level ACCPAY
 *
 * @returns {{ mode: 'documents'|'goods_receipt', jobs: Array, preservedExportedBills?: Array, resumeExports?: Array }}
 */
export function planXeroExportJobs({
  financeDocuments = [],
  venue = {},
  order = {},
  venueSupplier = {},
  receiptingProducts = [],
  forceSend = false,
  xeroAccounts = [],
  invoiceNumber,
  invoice,
  dueDate = null,
  supplierDefaultAccountId = null,
  /** Send-to-Xero UI contact when supplier has no defaultContact. */
  selectedContactId = null,
  /** When the invoice bill was just created in this session (for CN allocation). */
  fallbackXeroInvoiceId = null,
  xeroTaxRates = [],
} = {}) {
  const { invoices, creditNotes } = partitionFinanceDocuments(financeDocuments)
  const {
    pendingInvoices,
    pendingCreditNotes: pendingCredits,
    exportedInvoices,
    resumeExports = [],
  } = partitionPendingXeroExports(financeDocuments)

  const preservedExportedBills = exportedInvoices.map(({ document, link }) => ({
    omsDocumentId: link.omsDocumentId,
    xeroInvoiceId: link.xeroId,
    invoiceNumber: document?.invoiceNumber || null,
  }))

  const markResumeMeta = (doc, job) => {
    const resumeOpts = {
      isCreditNote: job.documentType === 'creditNote',
      expectAuditAttachment: true,
      expectHistoryNote: true,
    }
    if (needsXeroExportResume(doc, resumeOpts)) {
      return {
        ...job,
        resumeExport: true,
        existingXeroInvoiceId: String(doc?.xeroInvoiceId || '').trim(),
      }
    }
    return job
  }

  // Credit notes present → document jobs only (never net CN into the bill).
  // Part 14/15: skip docs that already have complete OMS↔Xero ids.
  // Part 16: incomplete exports stay in pending* and become resume jobs.
  if (creditNotes.length > 0) {
    const jobs = []
    for (const doc of pendingInvoices) {
      jobs.push(
        markResumeMeta(doc, {
          documentType: 'invoice',
          selectedInvoiceId: doc._id || doc.id,
          billData: buildXeroInvoiceExportBill({
            document: doc,
            venue,
            order,
            venueSupplier,
            receiptingProducts,
            forceSend,
            xeroAccounts,
            supplierDefaultAccountId,
            selectedContactId,
            dueDate,
            xeroTaxRates,
            invoiceNumber,
          }),
        }),
      )
    }
    // Prefer allocating new CNs to an already-exported bill on this PO.
    const preservedBillId =
      preservedExportedBills[0]?.xeroInvoiceId ||
      fallbackXeroInvoiceId ||
      order?.xeroInvoiceId ||
      null
    for (const doc of pendingCredits) {
      const allocateToXeroInvoiceId = resolveXeroCreditAllocationTarget({
        creditNote: doc,
        invoiceDocuments: invoices,
        fallbackXeroInvoiceId: preservedBillId,
      })
      jobs.push(
        markResumeMeta(doc, {
          documentType: 'creditNote',
          selectedInvoiceId: doc._id || doc.id,
          allocateToXeroInvoiceId,
          // Part 15 — explicit: CN export must not replace the bill job.
          preservesExistingBill: Boolean(allocateToXeroInvoiceId || preservedBillId),
          billData: buildXeroCreditNoteExportBill({
            document: doc,
            venue,
            order,
            venueSupplier,
            receiptingProducts,
            forceSend,
            xeroAccounts,
            supplierDefaultAccountId,
            selectedContactId,
            dueDate,
            allocateToXeroInvoiceId,
            xeroTaxRates,
          }),
        }),
      )
    }
    return {
      mode: 'documents',
      jobs,
      preservedExportedBills,
      resumeExports,
    }
  }

  // Invoice-only: export *pending* invoices only — never re-queue a complete bill.
  if (pendingInvoices.length > 0) {
    const jobs = pendingInvoices.map(doc =>
      markResumeMeta(doc, {
        documentType: 'invoice',
        selectedInvoiceId: doc._id || doc.id,
        billData: buildXeroInvoiceExportBill({
          document: doc,
          venue,
          order,
          venueSupplier,
          receiptingProducts,
          forceSend,
          xeroAccounts,
          supplierDefaultAccountId,
          selectedContactId,
          dueDate,
          xeroTaxRates,
          invoiceNumber,
        }),
      }),
    )
    return {
      mode: getConfirmedReceiptingRows(receiptingProducts).length
        ? 'goods_receipt'
        : 'documents',
      jobs,
      preservedExportedBills,
      resumeExports,
    }
  }

  // All linked finance documents already exported — nothing to create.
  if (invoices.length > 0 || creditNotes.length > 0) {
    return {
      mode: 'documents',
      jobs: [],
      preservedExportedBills,
      resumeExports,
    }
  }

  // No finance documents — GR-line ACCPAY (legacy), unless order already has a bill.
  if (isFinanceDocumentExportedToXero(order) || order?.sentToXero) {
    return {
      mode: 'goods_receipt',
      jobs: [],
      preservedExportedBills,
      resumeExports,
    }
  }

  const primaryDoc = invoice || null
  const billData = buildXeroInvoiceExportBill({
    document: primaryDoc || {},
    venue,
    order,
    venueSupplier,
    receiptingProducts,
    forceSend,
    xeroAccounts,
    supplierDefaultAccountId,
    selectedContactId,
    dueDate,
    xeroTaxRates,
    invoiceNumber,
  })
  return {
    mode: getConfirmedReceiptingRows(receiptingProducts).length
      ? 'goods_receipt'
      : primaryDoc
        ? 'documents'
        : 'goods_receipt',
    jobs: [
      {
        documentType: 'invoice',
        selectedInvoiceId: primaryDoc?._id || primaryDoc?.id || null,
        billData,
      },
    ],
    preservedExportedBills,
    resumeExports,
  }
}
